/**
 * Admin console › Permit equipment: the modules, inverters, microinverters and optimizers plan sets are sized with
 * (shipped from app/design/permits/data/equipment.yaml, plus what staff add). Add or edit an entry; its datasheet
 * values are JSON checked by the API per kind, and an unknown value stays null ("installer to confirm" on plan sets).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Button, Card, Field, Modal, PageHead, Select, Spinner, Switch, TextInput, useToast } from "../../components/ui";
import { useT } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";
import { DatasheetLibrary } from "./DatasheetLibrary";
import { AdminRackingDocuments, EquipmentDatasheet, usePermitDocuments } from "./PermitDocuments";
import { RefreshEquipment } from "./RefreshEquipment";

type Kind = S["PermitEquipmentKind"];
const KINDS: { value: Kind; label: MessageKey }[] = [
  { value: "module", label: "admin.permitEquipment.kindModule" },
  { value: "inverter", label: "admin.permitEquipment.kindInverter" },
  { value: "microinverter", label: "admin.permitEquipment.kindMicroinverter" },
  { value: "optimizer", label: "admin.permitEquipment.kindOptimizer" },
  { value: "battery", label: "admin.permitEquipment.kindBattery" },
];
const TEMPLATES: Record<Kind, object> = {
  module: { pmax_w: 430, voc_v: null, isc_a: null, vmp_v: null, imp_a: null, tc_voc_pct_per_c: null, tc_pmax_pct_per_c: null, max_system_voltage_v: 1000, max_series_fuse_a: 25 },
  inverter: { ac_rated_w: 7600, ac_voltage_v: 240, phases: 1, ac_max_current_a: null, max_dc_voltage_v: 600, mppt_count: 2, max_input_current_a: [15, 15], max_isc_a: null, mppt_min_v: null, mppt_max_v: null, rapid_shutdown: "none" },
  microinverter: { ac_rated_w: 300, ac_voltage_v: 240, ac_max_current_a: null, max_dc_voltage_v: 60, mppt_min_v: null, mppt_max_v: null, max_units_per_branch: null, branch_ocpd_a: 20, rapid_shutdown: "mlpe" },
  optimizer: { rated_w: 440, max_input_voltage_v: 60, max_input_isc_a: null, max_output_current_a: 15 },
  battery: { usable_kwh: 13.5, nameplate_kwh: null, max_continuous_power_kw: 5, nominal_voltage_v: null, coupling: "ac" },
};

interface Draft {
  id?: string;
  kind: Kind;
  manufacturer: string;
  model: string;
  markets: string;
  datasheet_url: string;
  verified: boolean;
  active: boolean;
  notes: string;
  specs: string;
}

function toDraft(e?: S["PermitEquipment"]): Draft {
  return e
    ? { id: e.id, kind: e.kind, manufacturer: e.manufacturer, model: e.model, markets: e.markets.join(", "), datasheet_url: e.datasheet_url ?? "", verified: e.verified, active: e.active, notes: e.notes ?? "", specs: JSON.stringify(e.specs, null, 2) }
    : { kind: "module", manufacturer: "", model: "", markets: "US", datasheet_url: "", verified: false, active: true, notes: "", specs: JSON.stringify(TEMPLATES.module, null, 2) };
}

export function AdminPermitEquipment() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const kinds = KINDS.map((k) => ({ value: k.value, label: t(k.label) }));
  const [kind, setKind] = useState<Kind | "all">("all");
  const [draft, setDraft] = useState<Draft | null>(null);
  const list = useQuery({
    queryKey: ["admin-permit-equipment"],
    queryFn: () => unwrap(api.GET("/v1/permit-equipment", { params: { query: { include_inactive: true } } })),
  });
  const save = useMutation({
    mutationFn: async (d: Draft) => {
      let specs: Record<string, unknown>;
      try {
        specs = JSON.parse(d.specs);
      } catch {
        throw new Error(t("admin.permitEquipment.invalidJson"));
      }
      const common = {
        manufacturer: d.manufacturer.trim(),
        model: d.model.trim(),
        markets: d.markets.split(/[\s,]+/).filter(Boolean).map((m) => m.toUpperCase()),
        datasheet_url: d.datasheet_url.trim() || null,
        verified: d.verified,
        active: d.active,
        notes: d.notes.trim() || null,
        specs,
      };
      return d.id
        ? unwrap(api.PATCH("/v1/admin/permit-equipment/{equipment_id}", { params: { path: { equipment_id: d.id } }, body: common }))
        : unwrap(api.POST("/v1/admin/permit-equipment", { body: { ...common, kind: d.kind } }));
    },
    onSuccess: (e) => {
      qc.invalidateQueries({ queryKey: ["admin-permit-equipment"] });
      qc.invalidateQueries({ queryKey: ["permit-equipment"] });
      setDraft(null);
      toast(t("admin.permitEquipment.saved", { manufacturer: e.manufacturer, model: e.model }));
    },
  });
  const items = (list.data?.items ?? []).filter((i) => kind === "all" || i.kind === kind);
  const docs = usePermitDocuments();
  return (
    <>
      <PageHead
        title={t("admin.permitEquipment.title")}
        description={t("admin.permitEquipment.description")}
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setDraft(toDraft())}>
            {t("admin.permitEquipment.add")}
          </Button>
        }
      />
      <RefreshEquipment />
      <Card
        pad={false}
        actions={
          <div style={{ width: 200 }}>
            <Select<Kind | "all"> value={kind} onChange={setKind} options={[{ value: "all", label: t("admin.permitEquipment.allKinds") }, ...kinds]} ariaLabel={t("admin.permitEquipment.colKind")} />
          </div>
        }
        title={t("admin.permitEquipment.count", { count: items.length })}
      >
        {list.isLoading ? (
          <Spinner />
        ) : list.error ? (
          <Alert tone="danger">{errorMessage(list.error)}</Alert>
        ) : (
          <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("admin.permitEquipment.colEquipment")}</th>
                <th>{t("admin.permitEquipment.colKind")}</th>
                <th>{t("admin.permitEquipment.colMarkets")}</th>
                <th>{t("admin.permitEquipment.colDatasheet")}</th>
                <th>{t("admin.permitEquipment.colStatus")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <td>
                    <span dir="auto"><b>{e.manufacturer}</b> {e.model}</span>
                    {e.notes && <div className="small muted" dir="auto">{e.notes}</div>}
                  </td>
                  <td>{kinds.find((k) => k.value === e.kind)?.label}</td>
                  <td dir="ltr">{e.markets.join(", ")}</td>
                  <td>
                    <EquipmentDatasheet item={e} />
                  </td>
                  <td>
                    <div className="row" style={{ gap: 4, flexWrap: "wrap" }}>
                      <Badge tone={e.verified ? "ok" : "warn"}>{e.verified ? t("admin.permitEquipment.verified") : t("admin.permitEquipment.checkValues")}</Badge>
                      {!e.active && <Badge>{t("admin.permitEquipment.hidden")}</Badge>}
                      {e.source === "shipped" && <Badge tone="info">{t("admin.permitEquipment.shipped")}</Badge>}
                    </div>
                  </td>
                  <td>
                    <Button size="sm" variant="ghost" icon={<Pencil size={14} />} onClick={() => setDraft(toDraft(e))}>
                      {t("admin.common.edit")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
      <AdminRackingDocuments docs={docs.data?.items} />
      <DatasheetLibrary />
      {draft && (
        <Modal
          title={draft.id ? t("admin.permitEquipment.editTitle", { manufacturer: draft.manufacturer, model: draft.model }) : t("admin.permitEquipment.add")}
          onClose={() => setDraft(null)}
          width={720}
          footer={
            <>
              <Button onClick={() => setDraft(null)}>{t("admin.common.cancel")}</Button>
              <Button variant="primary" loading={save.isPending} disabled={!draft.manufacturer.trim() || !draft.model.trim()} onClick={() => save.mutate(draft)}>
                {t("admin.common.save")}
              </Button>
            </>
          }
        >
          <div className="stack">
            {save.error && <Alert tone="danger">{errorMessage(save.error)}</Alert>}
            <div className="form-grid">
              <Field label={t("admin.permitEquipment.fieldKind")}>
                <Select<Kind> value={draft.kind} disabled={!!draft.id} onChange={(k) => setDraft({ ...draft, kind: k, specs: JSON.stringify(TEMPLATES[k], null, 2) })} options={kinds} />
              </Field>
              <Field label={t("admin.permitEquipment.fieldMarkets")} hint={t("admin.permitEquipment.fieldMarketsHint")}>
                <TextInput dir="ltr" value={draft.markets} onChange={(e) => setDraft({ ...draft, markets: e.target.value })} />
              </Field>
              <Field label={t("admin.permitEquipment.fieldManufacturer")}>
                <TextInput dir="auto" value={draft.manufacturer} onChange={(e) => setDraft({ ...draft, manufacturer: e.target.value })} />
              </Field>
              <Field label={t("admin.permitEquipment.fieldModel")}>
                <TextInput dir="auto" value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })} />
              </Field>
            </div>
            <Field label={t("admin.permitEquipment.fieldDatasheetUrl")} hint={t("admin.permitEquipment.fieldDatasheetUrlHint")}>
              <TextInput dir="ltr" value={draft.datasheet_url} onChange={(e) => setDraft({ ...draft, datasheet_url: e.target.value })} />
            </Field>
            <Field label={t("admin.permitEquipment.fieldSpecs")} hint={t("admin.permitEquipment.fieldSpecsHint")}>
              <textarea className="textarea code" dir="ltr" rows={12} style={{ minHeight: 240 }} value={draft.specs} onChange={(e) => setDraft({ ...draft, specs: e.target.value })} />
            </Field>
            <Field label={t("admin.permitEquipment.fieldNotes")}>
              <TextInput dir="auto" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </Field>
            <div className="row" style={{ gap: 24 }}>
              <Switch checked={draft.verified} onChange={(v) => setDraft({ ...draft, verified: v })} label={t("admin.permitEquipment.fieldVerified")} />
              <Switch checked={draft.active} onChange={(v) => setDraft({ ...draft, active: v })} label={t("admin.permitEquipment.fieldActive")} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
